import React from 'react';
import { cn } from '../../../lib/utils';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  
  // Additional className for the textarea element
  className?: string;
  
  //Error message to display below the textarea
  error?: string;
  
  // Label for the textarea
  label?: string;
  
  // Helper text to display below the textarea
  helperText?: string;
}

/**
 * Textarea Component
 * 
 * A reusable textarea component that follows the shadcn/ui design system guidelines.
 * - Uses the 8pt grid system for spacing
 * - Follows typography guidelines (Size 3/4 Regular)
 * - Uses semantic colors for theming
 */
const Textarea: React.FC<TextareaProps> = ({
  className,
  error,
  label,
  helperText,
  id,
  ...props
}) => {
  // Generate a unique ID if not provided
  const textareaId = id || `textarea-${Math.random().toString(36).substring(2, 9)}`;
  
  return (
    <div className="w-full">
      {/* Label - if provided */}
      {label && (
        <label 
          htmlFor={textareaId}
          className="block mb-2.5 text-sm font-medium text-foreground/90"
        >
          {label}
        </label>
      )}
      
      {/* Textarea element */}
      <textarea
        id={textareaId}
        className={cn(
          "w-full min-h-24 max-h-96 p-3.5 text-sm border border-gray-200 rounded-lg",
          "transition-colors duration-200",
          "focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 resize",
          "bg-background text-foreground placeholder:text-muted-foreground/60",
          error && "border-red-300 focus:border-red-400 focus:ring-red-100",
          className
        )}
        style={{ resize: "vertical" }}
        {...props}
      />
      
      {/* Error message - if provided */}
      {error && (
        <div className="mt-2.5 text-sm font-medium text-destructive/90">{error}</div>
      )}
      
      {/* Helper text - if provided */}
      {!error && helperText && (
        <div className="mt-2.5 text-xs text-muted-foreground/75">{helperText}</div>
      )}
    </div>
  );
};

export { Textarea };